/**
 * iter84.13 — Global Cloudflare Turnstile provider.
 *
 * One invisible (managed-mode) widget mounts at the App root. It silently
 * captures a token whenever Cloudflare hands one over, stashes the latest
 * token in a module-level ref, and auto-resets on consumption / expiry.
 *
 * The axios interceptor in `lib/api.js` reads `consumeTurnstileToken()`
 * for any POST to a Turnstile-gated path and attaches it as the
 * `cf-turnstile-response` header.
 *
 * Why a single global widget instead of per-form?
 *   - Tokens are short-lived (5 min) and single-use. With a global
 *     widget we pre-mint a token in the background so the user never
 *     waits for a fresh challenge — every submit ships immediately.
 *   - One mount = one Cloudflare script load = better page weight.
 *   - Per-form rollout would mean editing 5+ separate components;
 *     a global provider stays out of feature code entirely.
 *
 * Shadow-mode safety: if the widget hasn't yet produced a token (e.g.
 * Cloudflare slow to respond, ad-blocker, etc.) we return null and the
 * interceptor simply doesn't add the header — the backend in shadow
 * mode lets the request through and logs the absence.
 */
import { useEffect, useRef, useState } from "react";
import Turnstile, { useTurnstile } from "react-turnstile";

// Module-level token bag. Lives outside React state on purpose so the
// axios interceptor (which has no hook context) can read it.
let _latestToken = null;
let _resetFn = null;
let _onTokenReady = null;

export function consumeTurnstileToken() {
    const t = _latestToken;
    // Burn the token immediately so we never accidentally reuse it.
    // The widget's onVerify will refill _latestToken with a fresh one
    // after `reset()` triggers a re-challenge.
    _latestToken = null;
    if (typeof _resetFn === "function") {
        try { _resetFn(); } catch (_) { /* no-op */ }
    }
    return t;
}

export function isTurnstileReady() {
    return _latestToken !== null;
}

/**
 * Wait up to `timeoutMs` for a Turnstile token to be ready. Returns the
 * token (and consumes it) or null on timeout. Forms that want to require
 * a token before submit can `await waitForTurnstileToken(2000)` first.
 */
export function waitForTurnstileToken(timeoutMs = 2000) {
    if (_latestToken) return Promise.resolve(consumeTurnstileToken());
    return new Promise((resolve) => {
        const timer = setTimeout(() => {
            _onTokenReady = null;
            resolve(null);
        }, timeoutMs);
        _onTokenReady = () => {
            clearTimeout(timer);
            _onTokenReady = null;
            resolve(consumeTurnstileToken());
        };
    });
}

export default function TurnstileProvider() {
    const sitekey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
    const turnstile = useTurnstile();
    const widgetRef = useRef(null);
    // iter84.13b — Hide the widget wrapper once a token has landed.
    // Cloudflare's library keeps showing a "Success!" badge in
    // interaction-only mode; we collapse the wrapper so it disappears
    // entirely. The next consume() call resets the widget and a fresh
    // token is minted in the background; we re-show only if Cloudflare
    // escalates to a visible challenge again.
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Wire up the reset function so consumeTurnstileToken() can
        // request a fresh challenge. `useTurnstile()` returns an object
        // that may take a tick to populate; gate the assignment.
        if (turnstile && typeof turnstile.reset === "function") {
            _resetFn = turnstile.reset;
        }
        return () => { _resetFn = null; };
    }, [turnstile]);

    if (!sitekey) {
        // Missing site key → silently no-op. Backend in shadow mode will
        // log the absence; ops can investigate. We don't surface a UI
        // error because Turnstile is a defense-in-depth layer.
        return null;
    }

    return (
        <div
            data-testid="turnstile-provider"
            aria-hidden="true"
            // Wrapper hides after a token is captured so the "Success!"
            // badge doesn't linger on every page. `visibility: hidden`
            // (NOT `display: none`) is critical — Cloudflare needs the
            // iframe to stay mounted to keep the token bag warm in the
            // background; we only hide it visually.
            style={{
                position: "fixed",
                bottom: "8px",
                right: "8px",
                zIndex: 9999,
                visibility: visible ? "visible" : "hidden",
                pointerEvents: visible ? "auto" : "none",
            }}
        >
            <Turnstile
                ref={widgetRef}
                sitekey={sitekey}
                appearance="interaction-only"
                refreshExpired="auto"
                onLoad={() => {
                    // If Cloudflare decided to render the visible
                    // checkbox (suspicious traffic), the iframe will
                    // have non-zero dimensions; show the wrapper so
                    // the user can interact with it.
                    setTimeout(() => {
                        const iframe = widgetRef.current?.querySelector?.("iframe");
                        if (iframe && iframe.offsetHeight > 0) {
                            setVisible(true);
                        }
                    }, 100);
                }}
                onVerify={(token) => {
                    _latestToken = token || null;
                    // Token captured — collapse the wrapper. If Cloudflare
                    // needs another challenge later, onLoad will re-show it.
                    setVisible(false);
                    if (typeof _onTokenReady === "function") {
                        _onTokenReady();
                    }
                }}
                onExpire={() => {
                    _latestToken = null;
                }}
                onError={() => {
                    _latestToken = null;
                }}
            />
        </div>
    );
}
